import { FaListUl, FaImage } from "react-icons/fa6";
import { FaShareAlt, FaShopify, FaWordpress, FaMagento, FaSalesforce } from "react-icons/fa";
import { IoLayers } from "react-icons/io5";
import { MdEmail } from "react-icons/md";
import { HiDocumentText } from "react-icons/hi";

/**
 * Automation type definitions
 * Defines UI properties for each automation type
 */
export const AUTOMATION_TYPES = {
  listExtractor: {
    name: "List Extractor",
    icon: FaListUl,
    color: "yellow",
    colorClasses: {
      bg: "bg-yellow-400/10",
      iconTileBg: "bg-yellow-400/20",
      border: "border-yellow-400/20",
      text: "text-yellow-400",
      hoverBorder: "hover:border-yellow-400/40",
      shadow: "hover:shadow-yellow-400/10",
    },
    description: "Extract data from lists, tables, and paginated content",
  },
  pageExtractor: {
    name: "Page Details Extractor",
    icon: IoLayers,
    color: "blue",
    colorClasses: {
      bg: "bg-blue-400/10",
      iconTileBg: "bg-blue-400/20",
      border: "border-blue-400/20",
      text: "text-blue-400",
      hoverBorder: "hover:border-blue-400/40",
      shadow: "hover:shadow-blue-400/10",
    },
    description: "Extract specific data from a collection of pages",
  },
  emailExtractor: {
    name: "Email Extractor",
    icon: MdEmail,
    color: "green",
    colorClasses: {
      bg: "bg-green-400/10",
      iconTileBg: "bg-green-400/20",
      border: "border-green-400/20",
      text: "text-green-400",
      hoverBorder: "hover:border-green-400/40",
      shadow: "hover:shadow-green-400/10",
    },
    description: "Find and extract email addresses from pages",
  },
  socialLinkExtractor: {
    name: "Social Link Extractor",
    icon: FaShareAlt,
    color: "orange",
    colorClasses: {
      bg: "bg-orange-400/10",
      iconTileBg: "bg-orange-400/20",
      border: "border-orange-400/20",
      text: "text-orange-400",
      hoverBorder: "hover:border-orange-400/40",
      shadow: "hover:shadow-orange-400/10",
    },
    description: "Find social media links from pages",
  },
  imageExtractor: {
    name: "Image Downloader",
    icon: FaImage,
    color: "purple",
    colorClasses: {
      bg: "bg-purple-400/10",
      iconTileBg: "bg-purple-400/20",
      border: "border-purple-400/20",
      text: "text-purple-400",
      hoverBorder: "hover:border-purple-400/40",
      shadow: "hover:shadow-purple-400/10",
    },
    description: "Bulk download images from pages",
  },
  textExtractor: {
    name: "Text Extractor",
    icon: HiDocumentText,
    color: "zinc",
    colorClasses: {
      bg: "bg-zinc-200/10",
      iconTileBg: "bg-zinc-200/20",
      border: "border-zinc-200/20",
      text: "text-zinc-200",
      hoverBorder: "hover:border-zinc-200/40",
      shadow: "hover:shadow-zinc-200/10",
    },
    description: "Extract clean text content from pages",
  },
  shopifyExtractor: {
    name: "Shopify Extractor",
    icon: FaShopify,
    color: "shopify",
    colorClasses: {
      bg: "bg-shopify-400/10",
      iconTileBg: "bg-shopify-400/20",
      border: "border-shopify-400/20",
      text: "text-shopify-400",
      hoverBorder: "hover:border-shopify-400/40",
      shadow: "hover:shadow-shopify-400/10",
    },
    description: "Extract products and collections from Shopify stores",
  },
  wooCommerceExtractor: {
    name: "WooCommerce Extractor",
    icon: FaWordpress,
    color: "shopify",
    colorClasses: {
      bg: "bg-purple-400/10",
      iconTileBg: "bg-purple-400/20",
      border: "border-purple-400/20",
      text: "text-purple-400",
      hoverBorder: "hover:border-purple-400/40",
      shadow: "hover:shadow-purple-400/10",
    },
    description: "Extract products and categories from WooCommerce stores",
  },
  magentoExtractor: {
    name: "Magento Extractor",
    icon: FaMagento,
    color: "orange",
    // Reuses the Social Link Extractor's orange scale on purpose: Magento's brand colour is
    // orange, and Tailwind only ships classes it sees in source, so borrowing an existing
    // scale keeps these safe from purging.
    colorClasses: {
      bg: "bg-orange-400/10",
      iconTileBg: "bg-orange-400/20",
      border: "border-orange-400/20",
      text: "text-orange-400",
      hoverBorder: "hover:border-orange-400/40",
      shadow: "hover:shadow-orange-400/10",
    },
    description: "Extract products and categories from Magento / Adobe Commerce stores",
  },
  salesforceCommerceExtractor: {
    name: "Salesforce Commerce Extractor",
    icon: FaSalesforce,
    color: "sky",
    // Salesforce's brand blue (#00A1E0) sits closest to Tailwind's default `sky` scale; the
    // classes are written out literally so the JIT scanner keeps them (see tailwind.config.js
    // `content`). `sky` is distinct from the Page Extractor's `blue-400`.
    colorClasses: {
      bg: "bg-sky-400/10",
      iconTileBg: "bg-sky-400/20",
      border: "border-sky-400/20",
      text: "text-sky-400",
      hoverBorder: "hover:border-sky-400/40",
      shadow: "hover:shadow-sky-400/10",
    },
    description: "Extract products and categories from Salesforce Commerce Cloud stores",
  },
};

/**
 * Maps backend automation type strings to AUTOMATION_TYPES UI keys.
 */
export const TYPE_STRING_TO_UI_KEY = {
  extract_list: "listExtractor",
  extract_page_details: "pageExtractor",
  extract_pages: "pageExtractor",
  extract_google_maps_pages: "pageExtractor",
  extract_emails: "emailExtractor",
  extract_social_links: "socialLinkExtractor",
  extract_shopify: "shopifyExtractor",
  extract_woocommerce: "wooCommerceExtractor",
  extract_magento: "magentoExtractor",
  extract_salesforce_commerce: "salesforceCommerceExtractor",
};

/**
 * Structured-catalogue task types: MCP-authored, not editable in this dashboard, and the only
 * ones whose runs report progress through `run.catalogue` rather than URL counts. Adding a
 * platform means adding it here, not hunting for type literals across the view.
 */
export const CATALOGUE_TASK_TYPES = new Set(["extract_shopify", "extract_woocommerce", "extract_magento", "extract_salesforce_commerce"]);

export const isCatalogueTaskType = (type) => CATALOGUE_TASK_TYPES.has(type);
